import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { ArrowLeft, Loader2, Upload, CheckCircle2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import OpenPlaysLink from "@/components/OpenPlaysLink";
import BookingConditions from "@/components/BookingConditions";
import VenueRentalNotice from "@/components/VenueRentalNotice";
import { formatSlot } from "@/lib/formatSlot";

import { API } from "@/lib/apiBase";

const Payment = () => {
  const navigate = useNavigate();
  const { bookingId: urlBookingId } = useParams();
  const location = useLocation();
  const draft = location.state?.draft || null;
  // Multi-court "group" draft from /availability (1+ courts in one order).
  const groupDraft = location.state?.groupDraft || null;

  const [booking, setBooking] = useState(null);
  const [bookingId, setBookingId] = useState(urlBookingId || null);
  // Once a group booking has been created server-side, we track its id here.
  const [groupId, setGroupId] = useState(null);
  const [loading, setLoading] = useState(Boolean(urlBookingId));

  const [customerName, setCustomerName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");

  const [paymentTab, setPaymentTab] = useState("gcash");
  const [receiptFile, setReceiptFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const fetchBooking = useCallback(async () => {
    if (!urlBookingId) return;
    try {
      const response = await axios.get(`${API}/bookings/${urlBookingId}`);
      setBooking(response.data);
      setCustomerName(response.data.customer_name || "");
      setCustomerEmail(response.data.customer_email || "");
      setCustomerPhone(response.data.customer_phone || "");
    } catch {
      toast.error("Failed to load booking");
      navigate("/courts");
    } finally {
      setLoading(false);
    }
  }, [urlBookingId, navigate]);

  useEffect(() => {
    // Make sure the user lands on the Booking Summary at the top of the page
    window.scrollTo(0, 0);
    // Either we have a draft (new flow) or a bookingId in the URL (resume)
    if (urlBookingId) {
      fetchBooking();
    } else if (!draft && !groupDraft) {
      // No data — send back to select a court
      toast.error("Please start your booking from the courts page");
      navigate("/availability");
    }
  }, [urlBookingId, draft, groupDraft, fetchBooking, navigate]);

  const validateCustomerInfo = () => {
    if (!customerName || !customerEmail || !customerPhone) {
      toast.error("Please fill in your name, email, and mobile number");
      return false;
    }
    return true;
  };

  // Create booking on the server (returns the booking id) if we don't have one yet
  const ensureBookingExists = async () => {
    if (bookingId) return bookingId;
    const response = await axios.post(`${API}/bookings`, {
      court_id: draft.court_id,
      customer_name: customerName,
      customer_email: customerEmail,
      customer_phone: customerPhone,
      booking_date: draft.booking_date,
      time_slots: draft.time_slots,
    });
    setBookingId(response.data.id);
    setBooking(response.data);
    return response.data.id;
  };

  // Group equivalent: creates N bookings tied by `booking_group_id`.
  const ensureGroupExists = async () => {
    if (groupId) return groupId;
    const response = await axios.post(`${API}/bookings/group`, {
      items: groupDraft.items.map((it) => ({
        court_id: it.court_id,
        time_slots: it.time_slots,
      })),
      customer_name: customerName,
      customer_email: customerEmail,
      customer_phone: customerPhone,
      booking_date: groupDraft.booking_date,
    });
    setGroupId(response.data.booking_group_id);
    return response.data.booking_group_id;
  };

  const handleReceiptUpload = async () => {
    if (!agreedToTerms) {
      toast.error("Please agree to the Rental Conditions to continue");
      document.getElementById("terms-agree-checkbox")?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      return;
    }
    if (!validateCustomerInfo()) return;
    if (!receiptFile) {
      toast.error("Please select a receipt image first");
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", receiptFile);
      formData.append("payment_method", paymentTab);
      if (groupDraft) {
        const gid = await ensureGroupExists();
        await axios.post(`${API}/bookings/group/${gid}/receipt`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Receipt uploaded for your group booking. Awaiting confirmation.");
        navigate(`/booking-confirmation?group_id=${gid}&pending=true`);
      } else {
        const id = await ensureBookingExists();
        await axios.post(`${API}/bookings/${id}/receipt`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Receipt uploaded. Awaiting confirmation.");
        navigate(`/booking-confirmation?booking_id=${id}&pending=true`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload receipt");
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="inline-block w-8 h-8 border-4 border-[#8cc63f] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const summaryData = booking || draft;
  // Group flow: summarise across all courts in the group.
  if (!summaryData && !groupDraft) return null;

  const headerDate = (groupDraft?.booking_date || summaryData?.booking_date);
  const prettyDate = new Date(headerDate + "T00:00:00").toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
  const grandTotal = groupDraft
    ? groupDraft.total_amount
    : summaryData?.total_amount || 0;

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      {/* Header */}
      <header className="glass-header fixed top-0 left-0 right-0 z-50 px-6 py-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <h1
            className="flex items-center cursor-pointer"
            onClick={() => navigate("/")}
          >
            <img
              src="/assenda-logo.png"
              alt="Assenda"
              className="h-8 sm:h-10 w-auto object-contain"
              data-testid="brand-logo"
            />
          </h1>
          <div className="flex items-center gap-2 sm:gap-3">
            <OpenPlaysLink />
            <button
              data-testid="back-booking-button"
              onClick={() => navigate(-1)}
              className="text-sm font-semibold text-[#a1a1aa] hover:text-[#8cc63f] transition-colors flex items-center gap-2"
            >
              <ArrowLeft size={16} />
              Back
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 section-padding">
        <div className="max-w-3xl mx-auto">
          <div className="mb-6">
            <h1 className="text-2xl sm:text-3xl tracking-tighter font-black mb-1">
              Complete <span className="text-[#8cc63f]">Booking</span>
            </h1>
            <p className="text-sm text-[#a1a1aa]">
              Fill in your details then pick a payment method.
            </p>
          </div>

          {/* Booking summary */}
          <div className="card-surface p-6 mb-6" data-testid="booking-summary">
            <p className="label-text mb-3">Booking Summary</p>
            {groupDraft ? (
              // Multi-court group summary — each court with its slots & subtotal.
              <>
                <div className="flex flex-col sm:flex-row sm:justify-between gap-4 mb-4">
                  <div>
                    <p className="text-xl font-bold">
                      {groupDraft.items.length} courts
                    </p>
                    <p className="text-sm text-white font-semibold mt-2">{prettyDate}</p>
                  </div>
                  <div className="text-right">
                    <p className="label-text">Total</p>
                    <p
                      data-testid="group-summary-total"
                      className="text-3xl font-black text-[#8cc63f]"
                    >
                      ₱{grandTotal.toLocaleString()}
                    </p>
                  </div>
                </div>
                <div
                  data-testid="group-summary-items"
                  className="space-y-3 pt-3 border-t border-white/10"
                >
                  {groupDraft.items.map((it) => (
                    <div key={it.court_id} className="flex flex-col gap-1.5">
                      <div className="flex justify-between items-baseline">
                        <p className="font-bold text-white">{it.court_name}</p>
                        <p className="text-sm font-bold text-[#8cc63f]">
                          ₱{it.court_total.toLocaleString()}
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {it.time_slots.map((s) => (
                          <span
                            key={`${it.court_id}-${s}`}
                            className="text-[11px] bg-[#8cc63f]/15 text-[#8cc63f] px-2 py-0.5 rounded-sm font-semibold"
                          >
                            {formatSlot(s)}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <>
                <div className="flex flex-col sm:flex-row sm:justify-between gap-4 mb-4">
                  <div>
                    <p className="text-xl font-bold">{summaryData.court_name}</p>
                    <p className="text-sm text-white font-semibold mt-2">{prettyDate}</p>
                  </div>
                  <div className="text-right">
                    <p className="label-text">Total</p>
                    <p className="text-3xl font-black text-[#8cc63f]">
                      ₱{summaryData.total_amount.toLocaleString()}
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 pt-3 border-t border-white/10">
                  {summaryData.time_slots.map((s) => (
                    <span
                      key={s}
                      className="text-xs bg-[#8cc63f]/15 text-[#8cc63f] px-2 py-1 rounded-sm font-semibold"
                    >
                      {formatSlot(s)}
                    </span>
                  ))}
                </div>
              </>
            )}
            {(() => {
              // Court 5 is uncovered — surface the rain policy right inside
              // the booking summary so the customer sees it before paying.
              const names = groupDraft
                ? (groupDraft.items || []).map((it) => it.court_name)
                : summaryData
                  ? [summaryData.court_name]
                  : [];
              const hasCourt5 = names.some((n) => (n || "").toLowerCase() === "court 5");
              if (!hasCourt5) return null;
              return (
                <div
                  data-testid="court5-uncovered-notice"
                  className="mt-4 p-3 rounded-sm border border-amber-500/50 bg-amber-500/10 text-amber-200 text-xs sm:text-sm leading-relaxed"
                >
                  <strong className="block mb-1">
                    Please note that Court 5 is an UNCOVERED COURT.
                  </strong>
                  In case of rain, unused court hours will be refunded or,
                  subject to availability, transferred to a covered court.
                </div>
              );
            })()}
          </div>

          {/* Conditions — Standard Court Rental only. */}
          <BookingConditions
            agreed={agreedToTerms}
            onAgreedChange={setAgreedToTerms}
          />

          {/* Exclusive 4-court venue rental CTA — uses the shared
              VenueRentalNotice so the copy and Contact Us flow stay
              consistent across the entire customer journey. */}
          <VenueRentalNotice />

          {/* Your Information */}
          <div id="your-information" className="card-surface p-6 mb-6">
            <h2 className="text-xl font-bold mb-4">Your Information</h2>
            <div className="space-y-4">
              <div>
                <label className="label-text block mb-2">Full Name</label>
                <input
                  data-testid="customer-name-input"
                  type="text"
                  className="input-field"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  required
                  placeholder="Enter your full name"
                  disabled={Boolean(bookingId && booking)}
                />
              </div>
              <div>
                <label className="label-text block mb-2">Email Address</label>
                <input
                  data-testid="customer-email-input"
                  type="email"
                  className="input-field"
                  value={customerEmail}
                  onChange={(e) => setCustomerEmail(e.target.value)}
                  required
                  placeholder="your@email.com"
                  disabled={Boolean(bookingId && booking)}
                />
              </div>
              <div>
                <label className="label-text block mb-2">Mobile Number</label>
                <input
                  data-testid="customer-phone-input"
                  type="tel"
                  className="input-field"
                  value={customerPhone}
                  onChange={(e) => setCustomerPhone(e.target.value)}
                  required
                  placeholder="+63 912 345 6789"
                  pattern="[+0-9 ()-]{7,20}"
                  disabled={Boolean(bookingId && booking)}
                />
              </div>
            </div>
          </div>

          {/* Payment method tabs */}
          <div className="card-surface p-6">
            <p className="label-text mb-3">Payment Method</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-6">
              <button
                type="button"
                data-testid="pay-tab-gcash"
                onClick={() => setPaymentTab("gcash")}
                className={`p-3 border rounded-sm text-sm font-semibold transition-all ${
                  paymentTab === "gcash"
                    ? "bg-[#0074E0] text-white border-[#0074E0]"
                    : "bg-[#121212] text-white border-white/10 hover:border-[#0074E0]"
                }`}
              >
                GCash
              </button>
              <button
                type="button"
                data-testid="pay-tab-paymaya"
                onClick={() => setPaymentTab("paymaya")}
                className={`p-3 border rounded-sm text-sm font-semibold transition-all ${
                  paymentTab === "paymaya"
                    ? "bg-[#00D632] text-black border-[#00D632]"
                    : "bg-[#121212] text-white border-white/10 hover:border-[#00D632]"
                }`}
              >
                Maya
              </button>
              <button
                type="button"
                data-testid="pay-tab-bdo"
                onClick={() => setPaymentTab("bdo")}
                className={`p-3 border rounded-sm text-sm font-semibold transition-all ${
                  paymentTab === "bdo"
                    ? "bg-[#0033A0] text-white border-[#0033A0]"
                    : "bg-[#121212] text-white border-white/10 hover:border-[#0033A0]"
                }`}
              >
                BDO
              </button>
              <button
                type="button"
                data-testid="pay-tab-gotyme"
                onClick={() => setPaymentTab("gotyme")}
                className={`p-3 border rounded-sm text-sm font-semibold transition-all ${
                  paymentTab === "gotyme"
                    ? "bg-[#00C2C8] text-black border-[#00C2C8]"
                    : "bg-[#121212] text-white border-white/10 hover:border-[#00C2C8]"
                }`}
              >
                GoTyme
              </button>
            </div>

            {/* E-Wallet / Bank QR panels — all four QRs are Assenda's
                official InstaPay codes provided by the venue owner. */}
            <div className="bg-white rounded-md p-4 flex flex-col items-center min-h-[320px] justify-center">
                {paymentTab === "gcash" && (
                  <div data-testid="pay-panel-gcash" className="w-full flex justify-center">
                    <img
                      src="https://customer-assets.emergentagent.com/job_court-reserve-54/artifacts/69tq8ugu_Assenda_Gcash.png"
                      alt="Assenda Pickleball — GCash QR"
                      className="max-w-xs w-full h-auto"
                      data-testid="gcash-qr-image"
                    />
                  </div>
                )}
                {paymentTab === "paymaya" && (
                  <div data-testid="pay-panel-paymaya" className="w-full flex justify-center">
                    <img
                      src="https://customer-assets.emergentagent.com/job_court-reserve-54/artifacts/igkphs06_Assenda%20Maya.png"
                      alt="Assenda Pickleball — Maya QR"
                      className="max-w-xs w-full h-auto"
                      data-testid="maya-qr-image"
                    />
                  </div>
                )}
                {paymentTab === "bdo" && (
                  <div data-testid="pay-panel-bdo" className="w-full flex justify-center">
                    <img
                      src="https://customer-assets.emergentagent.com/job_court-reserve-54/artifacts/p32f6tou_Assenda_BDO%20New.png"
                      alt="Assenda Pickleball — BDO QR"
                      className="max-w-xs w-full h-auto"
                      data-testid="bdo-qr-image"
                    />
                  </div>
                )}
                {paymentTab === "gotyme" && (
                  <div data-testid="pay-panel-gotyme" className="w-full flex justify-center">
                    <img
                      src="https://customer-assets.emergentagent.com/job_court-reserve-54/artifacts/12iy64l7_Assenda_gotyme.png"
                      alt="Assenda Pickleball — GoTyme QR"
                      className="max-w-xs w-full h-auto"
                      data-testid="gotyme-qr-image"
                    />
                  </div>
                )}
              </div>

            <div className="mt-5 space-y-4">
              <p className="text-xs text-[#a1a1aa] text-center leading-relaxed">
                Scan the QR with the selected app and complete the transfer.
              </p>

                {/* Receipt upload */}
                <div className="p-5 bg-[#0a0a0a] border border-white/10 rounded-md">
                  <div className="flex items-center gap-2 mb-3">
                    <Upload size={18} className="text-[#8cc63f]" />
                    <p className="text-sm font-semibold">Upload Receipt</p>
                  </div>
                  <p className="text-xs text-[#a1a1aa] mb-4">
                    After transferring, upload your receipt screenshot.
                    Your booking will be confirmed once our team verifies the payment.
                  </p>

                  <label
                    htmlFor="receipt-input"
                    className="block cursor-pointer"
                  >
                    <div className="border-2 border-dashed border-white/20 hover:border-[#8cc63f] rounded-md p-6 text-center transition-colors">
                      {receiptFile ? (
                        <div className="flex flex-col items-center gap-2">
                          <CheckCircle2 className="text-[#8cc63f]" size={32} />
                          <p className="text-sm font-semibold text-white break-all px-2">
                            {receiptFile.name}
                          </p>
                          <p className="text-xs text-[#a1a1aa]">
                            {(receiptFile.size / 1024).toFixed(0)} KB · tap to change
                          </p>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center gap-2">
                          <Upload className="text-[#a1a1aa]" size={32} />
                          <p className="text-sm font-semibold">Tap to choose receipt</p>
                          <p className="text-xs text-[#a1a1aa]">
                            Any file type · max 3 MB
                          </p>
                        </div>
                      )}
                    </div>
                    <input
                      id="receipt-input"
                      data-testid="receipt-file-input"
                      type="file"
                      accept="*/*"
                      className="hidden"
                      onChange={(e) => setReceiptFile(e.target.files?.[0] || null)}
                    />
                  </label>

                  <button
                    data-testid="submit-receipt-button"
                    onClick={handleReceiptUpload}
                    disabled={uploading || !receiptFile || !agreedToTerms}
                    className="btn-primary w-full mt-4 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
                  >
                    {uploading ? (
                      <>
                        <Loader2 className="animate-spin" size={18} />
                        Uploading...
                      </>
                    ) : (
                      <>Submit Receipt & Wait for Confirmation</>
                    )}
                  </button>
                  {!agreedToTerms && (
                    <p
                      data-testid="terms-agree-hint"
                      className="text-[11px] text-amber-400 text-center mt-2"
                    >
                      Please tick the agreement checkbox above to continue.
                    </p>
                  )}
                </div>
              </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Payment;
